import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import HelpTooltip from '../../components/UI/HelpTooltip';
import { aboutAPI } from '../../services/api';
import { getStorageUrl } from '../../utils/imageHelpers';
import { Save, Upload, Image, Plus, X } from 'lucide-react';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.base};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0;
`;

const Form = styled.form`
  background-color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 2rem;
  box-shadow: ${props => props.theme.shadows.md};
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${props => props.theme.colors.border.primary};
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: ${props => props.rows ? props.rows * 1.5 : 6}rem;
  padding: 0.75rem;
  font-size: ${props => props.theme.typography.fontSize.base};
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
`;

const ImageUploadArea = styled.div`
  border: 2px dashed ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.primary}05;
  }
`;

const CurrentImage = styled.div`
  margin-top: 1rem;
  position: relative;
  
  img {
    max-width: 100%;
    max-height: 300px;
    border-radius: ${props => props.theme.borderRadius.md};
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const DeleteImageButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: ${props => props.theme.colors.error};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.full};
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: ${props => props.theme.shadows.md};
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.colors.errorDark || '#c62828'};
    transform: scale(1.1);
  }
`;

const ObjectivesContainer = styled.div`
  margin-top: 1rem;
`;

const ObjectiveItem = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  
  input {
    flex: 1;
  }
`;

const ServicesContainer = styled.div`
  margin-top: 1rem;
`;

const ServiceCard = styled.div`
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 1.5rem;
  margin-bottom: 1rem;
  background-color: ${props => props.theme.colors.background.secondary};
`;

const ServiceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ServiceNumber = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.primary};
`;

const ServiceFormGroup = styled.div`
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const RemoveButton = styled.button`
  background-color: ${props => props.theme.colors.error};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    opacity: 0.9;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: ${props => props.theme.colors.primary}15;
  color: ${props => props.theme.colors.primary};
  border: 1px solid ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 0.5rem 1rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
  cursor: pointer;
  margin-top: 0.5rem;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary}25;
  }
`;

const BoardContainer = styled.div`
  margin-top: 1rem;
`;

const BoardMemberCard = styled.div`
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 1.5rem;
  margin-bottom: 1rem;
  background-color: ${props => props.theme.colors.background.secondary};
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;

const BoardAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  background-color: ${props => props.theme.colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const BoardFields = styled.div`
  flex: 1;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Message = styled.div`
  padding: 1rem;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 1rem;
  background-color: ${props => props.type === 'success' 
    ? props.theme.colors.success + '20' 
    : props.theme.colors.error + '20'};
  color: ${props => props.type === 'success' 
    ? props.theme.colors.success 
    : props.theme.colors.error};
`;

const AboutManagement = () => {
  const [formData, setFormData] = useState({
    title: '',
    hero_subtitle: '',
    mission: '',
    vision: '',
    values: '',
    description: '',
    what_we_do: '',
    history: '',
    objectives: [],
    services: [],
    board_members: [],
    hero_slides: []
  });
  const [imageFile, setImageFile] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [sliderImages, setSliderImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      const response = await aboutAPI.get();
      const data = response.data.data;
      setFormData({
        title: data.title || '',
        hero_subtitle: data.hero_subtitle || '',
        mission: data.mission || '',
        vision: data.vision || '',
        values: data.values || '',
        description: data.description || '',
        what_we_do: data.what_we_do || '',
        history: data.history || '',
        objectives: data.objectives || [],
        services: data.services || [],
        board_members: data.board_members || [],
        hero_slides: data.hero_slides || []
      });
      setCurrentImageUrl(data.image_url || '');
    } catch (error) {
      console.error('Error fetching about:', error);
      showMessage('error', 'Failed to load content');
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleObjectiveChange = (index, value) => {
    const newObjectives = [...formData.objectives];
    newObjectives[index] = value;
    setFormData(prev => ({
      ...prev,
      objectives: newObjectives
    }));
  };

  const addObjective = () => {
    setFormData(prev => ({
      ...prev,
      objectives: [...prev.objectives, '']
    }));
  };

  const removeObjective = (index) => {
    setFormData(prev => ({
      ...prev,
      objectives: prev.objectives.filter((_, i) => i !== index)
    }));
  };

  const handleServiceChange = (index, field, value) => {
    const newServices = [...formData.services];
    newServices[index] = {
      ...newServices[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      services: newServices
    }));
  };

  const addService = () => {
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, { title: '', description: '', icon: '' }]
    }));
  };

  const removeService = (index) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  const handleBoardMemberChange = (index, field, value) => {
    const newMembers = [...formData.board_members];
    newMembers[index] = {
      ...newMembers[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      board_members: newMembers
    }));
  };

  const addBoardMember = () => {
    setFormData(prev => ({
      ...prev,
      board_members: [...prev.board_members, { name: '', position: '', bio: '', photo: '' }]
    }));
  };

  const removeBoardMember = (index) => {
    setFormData(prev => ({
      ...prev,
      board_members: prev.board_members.filter((_, i) => i !== index)
    }));
  };

  // Slider handlers
  const handleSliderImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('image', file);
      formData.append('title', '');

      const response = await aboutAPI.uploadSliderImage(formData);
      showMessage('success', 'Slider image uploaded!');
      
      // Add to form data
      setFormData(prev => ({
        ...prev,
        hero_slides: [...prev.hero_slides, { title: '', image: response.data.path }]
      }));
    } catch (error) {
      console.error('Error uploading slider image:', error);
      showMessage('error', 'Failed to upload slider image');
    } finally {
      setLoading(false);
      e.target.value = ''; // Reset input
    }
  };

  const handleSliderTitleChange = (index, title) => {
    setFormData(prev => ({
      ...prev,
      hero_slides: prev.hero_slides.map((slide, i) => 
        i === index ? { ...slide, title } : slide
      )
    }));
  };

  const handleSliderSubtitleChange = (index, subtitle) => {
    setFormData(prev => ({
      ...prev,
      hero_slides: prev.hero_slides.map((slide, i) => 
        i === index ? { ...slide, subtitle } : slide
      )
    }));
  };

  const removeSlide = (index) => {
    setFormData(prev => ({
      ...prev,
      hero_slides: prev.hero_slides.filter((_, i) => i !== index)
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
      // Preview image
      const reader = new FileReader();
      reader.onload = (event) => {
        setCurrentImageUrl(event.target.result);
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleDeleteImage = async () => {
    if (window.confirm('Are you sure you want to delete the featured image?')) {
      try {
        setLoading(true);
        
        // Delete the image via API
        await aboutAPI.deleteImage();
        
        // Clear the image locally
        setCurrentImageUrl('');
        setImageFile(null);
        
        showMessage('success', 'Image deleted successfully!');
        fetchAbout();
      } catch (error) {
        console.error('Error deleting image:', error);
        showMessage('error', 'Failed to delete image. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Update about content
      await aboutAPI.update(formData);

      // Upload image if selected
      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append('image', imageFile);
        await aboutAPI.uploadImage(imageFormData);
      }

      showMessage('success', 'About Us content updated successfully!');
      setImageFile(null);
      fetchAbout();
    } catch (error) {
      console.error('Error updating about:', error);
      showMessage('error', 'Failed to update content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>
          About Us Management
          <HelpTooltip
            steps={[
              {
                title: 'Step 1 – Describe the organization',
                content: (
                  <p>
                    Use this page to write the official description, mission, vision and values that appear on the public About page.
                    Keep the language clear and accessible for the public and partners.
                  </p>
                ),
              },
              {
                title: 'Step 2 – Add services and objectives',
                content: (
                  <ul>
                    <li>List the main services the network offers (for example, legal support, training, advocacy).</li>
                    <li>Add key objectives so visitors understand your priorities and areas of work.</li>
                  </ul>
                ),
              },
              {
                title: 'Step 3 – Maintain images and history',
                content: (
                  <ul>
                    <li>Upload a featured image and slider images to make the About page visually engaging.</li>
                    <li>Keep the history section updated as the organization grows.</li>
                  </ul>
                ),
              },
            ]}
          />
        </Title>
        <Subtitle>Manage your organization's about page content, services, and information</Subtitle>
      </Header>

      {message.text && (
        <Message type={message.type}>
          {message.text}
        </Message>
      )}

      <Form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <FormSection>
          <SectionTitle>Basic Information</SectionTitle>
          
          <FormGroup>
            <Label>Title</Label>
            <Input
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="About EWHRDN"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Hero Subtitle</Label>
            <Textarea
              name="hero_subtitle"
              value={formData.hero_subtitle}
              onChange={handleInputChange}
              rows="3"
              placeholder="Join the Ethiopian Women Human Rights Defenders Network and be part of a powerful movement for change"
            />
            <small style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.5rem', display: 'block' }}>
              This text appears below the hero title on the landing page
            </small>
          </FormGroup>

          <FormGroup>
            <Label>Description</Label>
            <Textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              placeholder="Main description of your organization..."
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>What We Do</Label>
            <Textarea
              name="what_we_do"
              value={formData.what_we_do}
              onChange={handleInputChange}
              rows={2}
              placeholder="Empowering women human rights defenders across Ethiopia through comprehensive support and resources"
            />
          </FormGroup>
        </FormSection>

        {/* Board Members */}
        <FormSection>
          <SectionTitle>Network Board</SectionTitle>
          <Subtitle>Manage board members (name, position, and photo uploaded from your computer).</Subtitle>
          <BoardContainer>
            {formData.board_members && formData.board_members.length > 0 ? (
              formData.board_members.map((member, index) => {
                const initials = (member.name || 'B')
                  .split(' ')
                  .map(n => n[0])
                  .join('')
                  .toUpperCase();

                const photoSrc = member.photo ? getStorageUrl(member.photo) : null;

                const handleBoardPhotoInputChange = async (event) => {
                  const file = event.target.files && event.target.files[0];
                  if (!file) return;

                  try {
                    setLoading(true);
                    const formDataFile = new FormData();
                    formDataFile.append('image', file);
                    const response = await aboutAPI.uploadBoardMemberPhoto(formDataFile);
                    const path = response.data?.path || response.data?.url;
                    if (path) {
                      handleBoardMemberChange(index, 'photo', path);
                      showMessage('success', 'Board member photo uploaded successfully.');
                    } else {
                      showMessage('error', 'Upload succeeded but no path returned from server.');
                    }
                  } catch (error) {
                    console.error('Error uploading board member photo:', error);
                    showMessage('error', 'Failed to upload board member photo. Please try again.');
                  } finally {
                    setLoading(false);
                    event.target.value = '';
                  }
                };

                return (
                  <BoardMemberCard key={index}>
                    <BoardAvatar theme={undefined}>
                      {photoSrc ? (
                        <img src={photoSrc} alt={member.name || 'Board member'} />
                      ) : (
                        initials
                      )}
                    </BoardAvatar>
                    <BoardFields>
                      <ServiceFormGroup>
                        <Label>Full Name *</Label>
                        <Input
                          value={member.name}
                          onChange={(e) => handleBoardMemberChange(index, 'name', e.target.value)}
                          required
                        />
                      </ServiceFormGroup>
                      <ServiceFormGroup>
                        <Label>Position / Role</Label>
                        <Input
                          value={member.position || ''}
                          onChange={(e) => handleBoardMemberChange(index, 'position', e.target.value)}
                          placeholder="e.g. Board Chair, Treasurer"
                        />
                      </ServiceFormGroup>
                      <ServiceFormGroup>
                        <Label>Bio</Label>
                        <Textarea
                          value={member.bio || ''}
                          onChange={(e) => handleBoardMemberChange(index, 'bio', e.target.value)}
                          placeholder="Brief biography of the board member..."
                          rows={4}
                        />
                      </ServiceFormGroup>
                      <ServiceFormGroup>
                        <Label>Photo</Label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              const input = document.getElementById(`boardPhotoInput-${index}`);
                              if (input) input.click();
                            }}
                          >
                            <Upload size={16} style={{ marginRight: '0.5rem' }} />
                            Upload Photo
                          </Button>
                          {member.photo && (
                            <span style={{ fontSize: '0.8rem', color: '#666' }}>
                              Photo set
                            </span>
                          )}
                        </div>
                        <input
                          id={`boardPhotoInput-${index}`}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={handleBoardPhotoInputChange}
                        />
                      </ServiceFormGroup>
                      <RemoveButton type="button" onClick={() => removeBoardMember(index)}>
                        <X size={16} />
                      </RemoveButton>
                    </BoardFields>
                  </BoardMemberCard>
                );
              })
            ) : (
              <p>No board members added yet.</p>
            )}
            <AddButton type="button" onClick={addBoardMember}>
              <Plus size={16} />
              Add Board Member
            </AddButton>
          </BoardContainer>
        </FormSection>

        {/* Mission, Vision, Values */}
        <FormSection>
          <SectionTitle>Mission, Vision & Values</SectionTitle>
          
          <FormGroup>
            <Label>Mission</Label>
            <Textarea
              name="mission"
              value={formData.mission}
              onChange={handleInputChange}
              rows={3}
              placeholder="Our mission statement..."
            />
          </FormGroup>

          <FormGroup>
            <Label>Vision</Label>
            <Textarea
              name="vision"
              value={formData.vision}
              onChange={handleInputChange}
              rows={3}
              placeholder="Our vision for the future..."
            />
          </FormGroup>

          <FormGroup>
            <Label>Values</Label>
            <Textarea
              name="values"
              value={formData.values}
              onChange={handleInputChange}
              rows={3}
              placeholder="Our core values..."
            />
          </FormGroup>
        </FormSection>

        {/* Services Section */}
        <FormSection>
          <SectionTitle>Our Services</SectionTitle>
          <Subtitle style={{ marginBottom: '1rem', fontSize: '0.9rem' }}>
            Add and manage the services your organization provides
          </Subtitle>
          
          <ServicesContainer>
            {formData.services.map((service, index) => (
              <ServiceCard key={index}>
                <ServiceHeader>
                  <ServiceNumber>Service {index + 1}</ServiceNumber>
                  <RemoveButton
                    type="button"
                    onClick={() => removeService(index)}
                    title="Remove Service"
                  >
                    <X size={16} />
                  </RemoveButton>
                </ServiceHeader>

                <ServiceFormGroup>
                  <Label>Service Icon (emoji or text)</Label>
                  <Input
                    value={service.icon || ''}
                    onChange={(e) => handleServiceChange(index, 'icon', e.target.value)}
                    placeholder="🛡️ or descriptive icon text"
                  />
                </ServiceFormGroup>

                <ServiceFormGroup>
                  <Label>Service Title</Label>
                  <Input
                    value={service.title || ''}
                    onChange={(e) => handleServiceChange(index, 'title', e.target.value)}
                    placeholder="e.g., Advocacy & Legal Support"
                    required
                  />
                </ServiceFormGroup>

                <ServiceFormGroup>
                  <Label>Service Description</Label>
                  <Textarea
                    value={service.description || ''}
                    onChange={(e) => handleServiceChange(index, 'description', e.target.value)}
                    rows={2}
                    placeholder="Describe this service..."
                    required
                  />
                </ServiceFormGroup>
              </ServiceCard>
            ))}
          </ServicesContainer>

          <AddButton type="button" onClick={addService}>
            <Plus size={16} />
            Add Service
          </AddButton>
        </FormSection>

        {/* Objectives */}
        <FormSection>
          <SectionTitle>Key Objectives</SectionTitle>
          
          <ObjectivesContainer>
            {formData.objectives.map((objective, index) => (
              <ObjectiveItem key={index}>
                <Input
                  value={objective}
                  onChange={(e) => handleObjectiveChange(index, e.target.value)}
                  placeholder={`Objective ${index + 1}`}
                />
                <RemoveButton
                  type="button"
                  onClick={() => removeObjective(index)}
                  title="Remove Objective"
                >
                  <X size={16} />
                </RemoveButton>
              </ObjectiveItem>
            ))}
          </ObjectivesContainer>

          <AddButton type="button" onClick={addObjective}>
            <Plus size={16} />
            Add Objective
          </AddButton>
        </FormSection>

        {/* History */}
        <FormSection>
          <SectionTitle>Our History</SectionTitle>
          
          <FormGroup>
            <Label>History</Label>
            <Textarea
              name="history"
              value={formData.history}
              onChange={handleInputChange}
              rows={4}
              placeholder="Tell the story of your organization..."
            />
          </FormGroup>
        </FormSection>

        {/* Hero Slider */}
        <FormSection>
          <SectionTitle>Hero Slider Images</SectionTitle>
          <Label>Add slider images with titles that will be displayed on the About Us page</Label>
          
          {formData.hero_slides && formData.hero_slides.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              {formData.hero_slides.map((slide, index) => (
                <ServiceCard key={index} style={{ marginBottom: '1rem' }}>
                  <ServiceHeader>
                    <ServiceNumber>Slide {index + 1}</ServiceNumber>
                    <RemoveButton
                      type="button"
                      onClick={() => removeSlide(index)}
                      title="Remove Slide"
                    >
                      <X size={16} />
                    </RemoveButton>
                  </ServiceHeader>
                  
                  <FormGroup>
                    <Label>Slide Title</Label>
                    <Input
                      type="text"
                      value={slide.title || ''}
                      onChange={(e) => handleSliderTitleChange(index, e.target.value)}
                      placeholder="Enter slide title (e.g., 'Empowering Women Defenders')"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Slide Subtitle</Label>
                    <Textarea
                      value={slide.subtitle || ''}
                      onChange={(e) => handleSliderSubtitleChange(index, e.target.value)}
                      rows="2"
                      placeholder="Enter slide subtitle (optional - if empty, uses global hero subtitle)"
                    />
                  </FormGroup>
                  
                  {slide.image && (
                    <div style={{ marginTop: '1rem' }}>
                      <img 
                        src={getStorageUrl(slide.image)}
                        alt={`Slide ${index + 1}`}
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '200px', 
                          borderRadius: '8px',
                          objectFit: 'cover'
                        }}
                      />
                    </div>
                  )}
                </ServiceCard>
              ))}
            </div>
          )}
          
          <FormGroup>
            <Label>Upload New Slider Image</Label>
            <ImageUploadArea onClick={() => document.getElementById('sliderImageInput').click()}>
              <Image size={48} style={{ margin: '0 auto 1rem' }} />
              <p>Click to upload a slider image</p>
              <p style={{ fontSize: '0.875rem', opacity: 0.7 }}>
                JPG, PNG or GIF (Max 5MB) - Recommended: 1920x500px
              </p>
            </ImageUploadArea>
            <input
              id="sliderImageInput"
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleSliderImageUpload}
            />
          </FormGroup>
        </FormSection>

        {/* Featured Image */}
        <FormSection>
          <SectionTitle>Featured Image</SectionTitle>
          
          <FormGroup>
            <Label>Upload Image</Label>
            <ImageUploadArea onClick={() => document.getElementById('imageInput').click()}>
              <Image size={48} style={{ margin: '0 auto 1rem' }} />
              <p>Click to upload an image</p>
              <p style={{ fontSize: '0.875rem', opacity: 0.7 }}>
                {imageFile ? imageFile.name : 'JPG, PNG or GIF (Max 5MB)'}
              </p>
            </ImageUploadArea>
            <input
              id="imageInput"
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageChange}
            />
            
            {currentImageUrl && (
              <CurrentImage>
                <Label>Current Image:</Label>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img src={currentImageUrl.startsWith('data:') ? currentImageUrl : getStorageUrl(currentImageUrl)} alt="About Us" />
                  <DeleteImageButton
                    type="button"
                    onClick={handleDeleteImage}
                    title="Delete Image"
                  >
                    <X size={20} />
                  </DeleteImageButton>
                </div>
              </CurrentImage>
            )}
          </FormGroup>
        </FormSection>

        <ButtonGroup>
          <Button
            type="submit"
            variant="primary"
            size="large"
            disabled={loading}
          >
            <Save size={20} style={{ marginRight: '0.5rem' }} />
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
          
          <Button
            type="button"
            variant="outline"
            size="large"
            onClick={fetchAbout}
            disabled={loading}
          >
            Reset
          </Button>
        </ButtonGroup>
      </Form>
    </Container>
  );
};

export default AboutManagement;
